/**
 * ProjectileRenderer - Visualizes weapon projectiles
 */

import * as THREE from 'three';
import { WeaponType, type Projectile } from '../systems/WeaponSystem';

export class ProjectileRenderer {
  private scene: THREE.Scene;
  private projectileMeshes: Map<string, THREE.Object3D>;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.projectileMeshes = new Map();
  }

  update(projectiles: Projectile[]): void {
    const activeIds = new Set<string>();

    // Update or create meshes for active projectiles
    projectiles.forEach((projectile) => {
      if (!projectile.active) return;

      activeIds.add(projectile.id);

      let mesh = this.projectileMeshes.get(projectile.id);

      if (!mesh) {
        mesh = this.createProjectileMesh(projectile.type);
        this.projectileMeshes.set(projectile.id, mesh);
        this.scene.add(mesh);
      }

      // Update position
      mesh.position.set(
        projectile.position.x,
        projectile.position.y,
        projectile.position.z
      );

      // Update rotation to face velocity direction
      const velocity = projectile.velocity;
      const speed = Math.sqrt(velocity.x ** 2 + velocity.y ** 2 + velocity.z ** 2);
      if (speed > 0.1) {
        const direction = new THREE.Vector3(
          velocity.x / speed,
          velocity.y / speed,
          velocity.z / speed
        );
        mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), direction);
      }

      // Add trail effect for missiles
      if (projectile.type === WeaponType.HELLFIRE || projectile.type === WeaponType.ROCKETS) {
        this.addTrailEffect(projectile.position);
      }
    });

    // Remove meshes for inactive projectiles
    this.projectileMeshes.forEach((mesh, id) => {
      if (!activeIds.has(id)) {
        this.scene.remove(mesh);
        this.projectileMeshes.delete(id);
      }
    });
  }

  private createProjectileMesh(type: WeaponType): THREE.Object3D {
    const group = new THREE.Group();

    switch (type) {
      case WeaponType.CHAIN_GUN:
        // Small yellow tracer
        const tracerGeometry = new THREE.CylinderGeometry(0.05, 0.05, 0.5, 4);
        const tracerMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00 });
        const tracer = new THREE.Mesh(tracerGeometry, tracerMaterial);
        tracer.rotation.x = Math.PI / 2;
        group.add(tracer);
        break;

      case WeaponType.ROCKETS:
        // Rocket body
        const rocketBodyGeometry = new THREE.CylinderGeometry(0.1, 0.1, 1.5, 8);
        const rocketMaterial = new THREE.MeshPhongMaterial({
          color: 0x808080,
          flatShading: true,
        });
        const rocketBody = new THREE.Mesh(rocketBodyGeometry, rocketMaterial);
        rocketBody.rotation.x = Math.PI / 2;
        group.add(rocketBody);

        // Rocket fins
        const finGeometry = new THREE.BoxGeometry(0.3, 0.05, 0.5);
        for (let i = 0; i < 4; i++) {
          const fin = new THREE.Mesh(finGeometry, rocketMaterial);
          fin.position.z = 0.5;
          fin.rotation.y = (i * Math.PI) / 2;
          group.add(fin);
        }

        // Exhaust glow
        const exhaustGeometry = new THREE.SphereGeometry(0.15, 8, 8);
        const exhaustMaterial = new THREE.MeshBasicMaterial({ color: 0xff6600 });
        const exhaust = new THREE.Mesh(exhaustGeometry, exhaustMaterial);
        exhaust.position.z = 0.8;
        group.add(exhaust);
        break;

      case WeaponType.HELLFIRE:
        // Hellfire missile body
        const hellfireBodyGeometry = new THREE.CylinderGeometry(0.08, 0.08, 1.8, 8);
        const hellfireMaterial = new THREE.MeshPhongMaterial({
          color: 0x404040,
          flatShading: true,
        });
        const hellfireBody = new THREE.Mesh(hellfireBodyGeometry, hellfireMaterial);
        hellfireBody.rotation.x = Math.PI / 2;
        group.add(hellfireBody);

        // Warhead
        const warheadGeometry = new THREE.ConeGeometry(0.08, 0.3, 8);
        const warhead = new THREE.Mesh(warheadGeometry, hellfireMaterial);
        warhead.rotation.x = -Math.PI / 2;
        warhead.position.z = -1;
        group.add(warhead);

        // Guidance section
        const guidanceGeometry = new THREE.BoxGeometry(0.15, 0.15, 0.4);
        const guidance = new THREE.Mesh(guidanceGeometry, hellfireMaterial);
        guidance.position.z = -0.5;
        group.add(guidance);

        // Exhaust glow
        const hellfireExhaustGeometry = new THREE.SphereGeometry(0.12, 8, 8);
        const hellfireExhaustMaterial = new THREE.MeshBasicMaterial({ color: 0xff8800 });
        const hellfireExhaust = new THREE.Mesh(hellfireExhaustGeometry, hellfireExhaustMaterial);
        hellfireExhaust.position.z = 1;
        group.add(hellfireExhaust);
        break;
    }

    return group;
  }

  private addTrailEffect(position: { x: number; y: number; z: number }): void {
    // Create a simple trail particle (smoke)
    const particleGeometry = new THREE.SphereGeometry(0.3, 6, 6);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: 0xaaaaaa,
      transparent: true,
      opacity: 0.6,
    });
    const particle = new THREE.Mesh(particleGeometry, particleMaterial);
    particle.position.set(position.x, position.y, position.z);

    this.scene.add(particle);

    // Fade out and remove after a short time
    setTimeout(() => {
      this.scene.remove(particle);
    }, 1000);
  }

  dispose(): void {
    this.projectileMeshes.forEach((mesh) => {
      this.scene.remove(mesh);
    });
    this.projectileMeshes.clear();
  }
}
